import {tealiumConfig} from './tealiumConfig';
import './types';

const SCRIPT_ID = 'tealium-utag';

let loadingPromise: Promise<void> | null = null;

/**
 * File d'attente des évènements émis avant que utag.js soit prêt.
 * On rejoue à l'init dans l'ordre. Limité à 50 évènements pour éviter les fuites.
 */
type QueuedEvent =
  | {type: 'view'; data?: Record<string, unknown>}
  | {type: 'link'; data?: Record<string, unknown>};
const eventQueue: QueuedEvent[] = [];
const MAX_QUEUE = 50;

const isBrowser = (): boolean => typeof window !== 'undefined';

export const isTealiumReady = (): boolean =>
  isBrowser() && typeof window.utag !== 'undefined';

export const enqueueTealiumEvent = (event: QueuedEvent): void => {
  if (eventQueue.length >= MAX_QUEUE) return;
  eventQueue.push(event);
};

const flushQueue = () => {
  if (!isTealiumReady()) return;
  while (eventQueue.length) {
    const event = eventQueue.shift();
    if (!event) continue;
    try {
      if (event.type === 'view') {
        window.utag?.view(event.data);
      } else {
        window.utag?.link(event.data);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[tealium] flush failed', err);
    }
  }
};

/**
 * Charge utag.js de manière asynchrone.
 * Idempotent : retourne la même promesse si déjà en cours / chargé.
 *
 * Important : on doit positionner `utag_cfg_ovrd.noview = true` AVANT le
 * chargement pour désactiver le page view automatique (mode SPA).
 */
export const initTealium = (): Promise<void> => {
  if (!isBrowser()) return Promise.resolve();

  const {account, profile, env} = tealiumConfig;
  if (!account || !profile) {
    if (tealiumConfig.debug) {
      // eslint-disable-next-line no-console
      console.info('[tealium] account/profile manquants, init ignorée');
    }
    return Promise.resolve();
  }

  if (isTealiumReady()) return Promise.resolve();
  if (loadingPromise) return loadingPromise;

  loadingPromise = new Promise<void>((resolve, reject) => {
    window.utag_cfg_ovrd = {
      ...(window.utag_cfg_ovrd || {}),
      noview: true,
    };
    window.utag_data = window.utag_data || {};

    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => {
        flushQueue();
        resolve();
      });
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.type = 'text/javascript';
    script.src = `https://tags.tiqcdn.com/utag/${account}/${profile}/${env}/utag.js`;
    script.onload = () => {
      if (tealiumConfig.debug) {
        // eslint-disable-next-line no-console
        console.info('[tealium] utag.js chargé', {account, profile, env});
      }
      flushQueue();
      resolve();
    };
    script.onerror = err => {
      // eslint-disable-next-line no-console
      console.warn('[tealium] échec du chargement utag.js', err);
      loadingPromise = null;
      reject(err);
    };

    document.head.appendChild(script);
  });

  return loadingPromise;
};

/**
 * À appeler quand le consentement est retiré : ne supprime pas le script déjà
 * chargé (utag.js ne propose pas de "destroy"), mais coupe les nouveaux events.
 */
export const stopTealium = (): void => {
  eventQueue.length = 0;
};
